import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Quiz, QuizResponse, QuizAnswer, Question } from '@/types/quiz';
import { QuizResponseStatus } from '@/types/quiz';

/**
 * Quiz Slice - Manages quiz state
 * - Loading quizzes from templates
 * - Navigating through questions
 * - Saving user responses
 * - Submitting completed quizzes
 */

interface QuizState {
  // Current quiz being taken
  activeQuiz: Quiz | null;
  activeResponse: QuizResponse | null;

  // Navigation state
  currentQuestionIndex: number;
  totalQuestions: number;

  // Submission state
  isSubmitting: boolean;
  submitError: string | null;
  lastSubmissionId: string | null;

  // Retry/cooldown state
  canRetry: boolean;
  lastSubmitAttempt: number;

  // Quiz templates (for admin/future use)
  availableQuizzes: Quiz[];
  isLoadingQuizzes: boolean;

  // Progress tracking
  startTime: number | null;
  elapsedTime: number;
}

const initialState: QuizState = {
  activeQuiz: null,
  activeResponse: null,
  currentQuestionIndex: 0,
  totalQuestions: 0,
  isSubmitting: false,
  submitError: null,
  lastSubmissionId: null,
  canRetry: true,
  lastSubmitAttempt: 0,
  availableQuizzes: [],
  isLoadingQuizzes: false,
  startTime: null,
  elapsedTime: 0,
};

const quizSlice = createSlice({
  name: 'quiz',
  initialState,
  reducers: {
    // Load a quiz template
    loadQuiz: (state, action: PayloadAction<Quiz>) => {
      state.activeQuiz = action.payload;
      state.totalQuestions = action.payload.questions.length;
      state.currentQuestionIndex = 0;
      state.startTime = Date.now();
      state.elapsedTime = 0;

      // Initialize response
      state.activeResponse = {
        id: `response-${Date.now()}`,
        quizId: action.payload.id,
        quizVersion: action.payload.version,
        userId: '', // Will be set when user provides email
        status: QuizResponseStatus.InProgress,
        startedAt: new Date().toISOString(),
        timeSpent: 0,
        answers: [],
      };
    },

    // Update user ID (email or internal ID)
    setUserId: (state, action: PayloadAction<string>) => {
      if (state.activeResponse) {
        state.activeResponse.userId = action.payload;
      }
    },

    // Navigate to specific question
    goToQuestion: (state, action: PayloadAction<number>) => {
      const index = action.payload;
      if (index >= 0 && index < state.totalQuestions) {
        state.currentQuestionIndex = index;
      }
    },

    // Navigate to next question
    nextQuestion: (state) => {
      if (state.currentQuestionIndex < state.totalQuestions - 1) {
        state.currentQuestionIndex += 1;
      }
    },

    // Navigate to previous question
    previousQuestion: (state) => {
      if (state.currentQuestionIndex > 0) {
        state.currentQuestionIndex -= 1;
      }
    },

    // Save answer for current question
    saveAnswer: (state, action: PayloadAction<{ questionId: string; value: string | string[] | number | null }>) => {
      if (!state.activeResponse) return;

      const { questionId, value } = action.payload;
      const existingAnswerIndex = state.activeResponse.answers.findIndex(
        (a) => a.questionId === questionId
      );

      const answer: QuizAnswer = {
        questionId,
        value,
        answeredAt: new Date().toISOString(),
      };

      if (existingAnswerIndex >= 0) {
        // Update existing answer
        state.activeResponse.answers[existingAnswerIndex] = answer;
      } else {
        // Add new answer
        state.activeResponse.answers.push(answer);
      }
    },

    // Update elapsed time
    updateElapsedTime: (state) => {
      if (state.startTime) {
        state.elapsedTime = Math.floor((Date.now() - state.startTime) / 1000);
        if (state.activeResponse) {
          state.activeResponse.timeSpent = state.elapsedTime;
        }
      }
    },

    // Set submission state
    setSubmitting: (state, action: PayloadAction<boolean>) => {
      state.isSubmitting = action.payload;
    },

    // Set submit error
    setSubmitError: (state, action: PayloadAction<string | null>) => {
      state.submitError = action.payload;
    },

    // Set retry cooldown
    setRetryState: (state, action: PayloadAction<{ canRetry: boolean; lastAttempt?: number }>) => {
      state.canRetry = action.payload.canRetry;
      if (action.payload.lastAttempt !== undefined) {
        state.lastSubmitAttempt = action.payload.lastAttempt;
      }
    },

    // Complete quiz
    completeQuiz: (state, action: PayloadAction<{ result?: string; score?: number; submissionId?: string }>) => {
      if (state.activeResponse) {
        state.activeResponse.status = QuizResponseStatus.Completed;
        state.activeResponse.completedAt = new Date().toISOString();
        state.activeResponse.result = action.payload.result;
        state.activeResponse.score = action.payload.score;
        state.lastSubmissionId = action.payload.submissionId || null;
      }
    },

    // Reset quiz (start over)
    resetQuiz: (state) => {
      state.activeQuiz = null;
      state.activeResponse = null;
      state.currentQuestionIndex = 0;
      state.totalQuestions = 0;
      state.isSubmitting = false;
      state.submitError = null;
      state.startTime = null;
      state.elapsedTime = 0;
    },

    // Load quiz templates (for admin)
    setAvailableQuizzes: (state, action: PayloadAction<Quiz[]>) => {
      state.availableQuizzes = action.payload;
    },

    setLoadingQuizzes: (state, action: PayloadAction<boolean>) => {
      state.isLoadingQuizzes = action.payload;
    },
  },
});

export const {
  loadQuiz,
  setUserId,
  goToQuestion,
  nextQuestion,
  previousQuestion,
  saveAnswer,
  updateElapsedTime,
  setSubmitting,
  setSubmitError,
  setRetryState,
  completeQuiz,
  resetQuiz,
  setAvailableQuizzes,
  setLoadingQuizzes,
} = quizSlice.actions;

export default quizSlice.reducer;

// Selectors
export const selectCurrentQuestion = (state: { quiz: QuizState }): Question | null => {
  if (!state.quiz.activeQuiz) return null;
  return state.quiz.activeQuiz.questions[state.quiz.currentQuestionIndex] || null;
};

export const selectAnswerForQuestion = (questionId: string) => (state: { quiz: QuizState }) => {
  return state.quiz.activeResponse?.answers.find(a => a.questionId === questionId) || null;
};

export const selectProgress = (state: { quiz: QuizState }) => {
  if (state.quiz.totalQuestions === 0) return 0;
  return ((state.quiz.currentQuestionIndex + 1) / state.quiz.totalQuestions) * 100;
};

export const selectIsQuizComplete = (state: { quiz: QuizState }) => {
  return state.quiz.activeResponse?.status === QuizResponseStatus.Completed;
};

export const selectAnsweredQuestions = (state: { quiz: QuizState }) => {
  return state.quiz.activeResponse?.answers.length || 0;
};
